import type React from "react";
import { createContext, useContext, useEffect } from "react";

import * as Sentry from "@sentry/react";
import { isAvatarSelectionKey, type AvatarSelectionKey } from "@stacklet/ui";
import { graphql, useFragment } from "react-relay";

import type {
  UserContext_platform$key,
  UserContext_platform$data,
  Permission as RoleScopePermission,
  CloudCostType,
} from "./__generated__/UserContext_platform.graphql";

export interface UserContextType {
  roleScopes: UserContext_platform$data["platform"]["roleScopes"];
  costReportingConfig: UserContext_platform$data["platform"]["costReportingConfig"];
  currentUser: UserContext_platform$data["whoami"]["user"] & {
    displayName: UserContext_platform$data["whoami"]["displayName"];
    email: UserContext_platform$data["whoami"]["email"];
    /** The user's stored avatar color/gravatar preference, or undefined if never set */
    avatarSelection?: AvatarSelectionKey;
  };
}

interface Props {
  children: React.ReactNode;
  query: UserContext_platform$key;
}

export type { RoleScopePermission as Permission, CloudCostType };

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserContextProvider = ({ children, query }: Props) => {
  const data = useFragment(
    graphql`
      fragment UserContext_platform on Query {
        platform {
          costReportingConfig {
            awsCostType
            azureCostType
            gcpCostType
          }
          roleScopes {
            accountGroup {
              userPermissions
            }
            policyCollection {
              userPermissions
            }
            repository {
              userPermissions
            }
            system {
              userPermissions
            }
            userGroup {
              userPermissions
            }
          }
        }
        whoami {
          email
          displayName
          user {
            ssoUser
          }
        }
        avatarColor: userData(
          filterElement: {
            single: { name: "name", operator: "equals", value: "avatar_color" }
          }
        ) {
          edges {
            node {
              value
            }
          }
        }
      }
    `,
    query,
  );

  let roleScopes: UserContextType["roleScopes"] = {
    accountGroup: { userPermissions: [] },
    policyCollection: { userPermissions: [] },
    repository: { userPermissions: [] },
    system: { userPermissions: [] },
    userGroup: { userPermissions: [] },
  };

  let costReportingConfig: UserContextType["costReportingConfig"] = {
    awsCostType: "EffectiveCost",
    azureCostType: "BilledCost",
    gcpCostType: "EffectiveCost",
  };

  if (data?.platform?.roleScopes) {
    const sourceScopes = data.platform.roleScopes;
    roleScopes = {
      accountGroup: {
        userPermissions: sourceScopes.accountGroup?.userPermissions || [],
      },
      policyCollection: {
        userPermissions: sourceScopes.policyCollection?.userPermissions || [],
      },
      repository: {
        userPermissions: sourceScopes.repository?.userPermissions || [],
      },
      system: {
        userPermissions: sourceScopes.system?.userPermissions || [],
      },
      userGroup: {
        userPermissions: sourceScopes.userGroup?.userPermissions || [],
      },
    };
  }

  if (data?.platform?.costReportingConfig) {
    const sourceCostConfig = data.platform.costReportingConfig;
    costReportingConfig = {
      awsCostType: sourceCostConfig.awsCostType,
      azureCostType: sourceCostConfig.azureCostType,
      gcpCostType: sourceCostConfig.gcpCostType,
    };
  }

  const avatarColorValue = data.avatarColor.edges?.[0]?.node.value;
  const avatarSelection =
    avatarColorValue && isAvatarSelectionKey(avatarColorValue)
      ? avatarColorValue
      : undefined;

  // Manually mapped (rather than spreading `data.whoami`) to avoid unused-fields relay lint errors.
  const currentUser = {
    ssoUser: data.whoami.user.ssoUser,
    displayName: data.whoami.displayName,
    email: data.whoami.email,
    avatarSelection,
  };

  useEffect(() => {
    Sentry.setUser({
      email: data.whoami.email ?? undefined,
      username: data.whoami.displayName ?? undefined,
    });
    return () => {
      Sentry.setUser(null);
    };
  }, [data.whoami.email, data.whoami.displayName]);

  return (
    <UserContext.Provider
      value={{ roleScopes, costReportingConfig, currentUser }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};
